/**
 * CloseDateCard.tsx — ✦ Close date check (deal detail slice 8; spec §3.5 B;
 * R2). The rules — when it shows, when a Keep hides it — are closeDateCheck.ts.
 *
 * "Change close date" opens a date input and saves ONE field through the
 * existing deal PUT (the Details row's own body, `expectedClose` at UTC
 * midnight). "Keep {d Mon}" calls POST …/close-date/confirm (0133, R2: the
 * date confirmed and the moment, a close_date_confirmed history row — the
 * timeline's "Close date confirmed"); the card then gives way to the spec's
 * sentence. A refusal shows the server's own sentence.
 */
import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import type { DealDetail } from '../../types'
import { updateDeal, confirmDealCloseDate } from '../../api'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { apiErrorMessage } from '../../../../api/client'
import { danger, info } from '../../../../theme/tokens'
import { KEPT_SENTENCE, type CloseDateCardView } from './closeDateCheck'
import { closeDay } from './dealHealthCard'

export function CloseDateCard({ deal, view, onApply }: {
  deal: DealDetail
  view: CloseDateCardView | null
  onApply: (write: Promise<DealDetail>) => Promise<void>
}) {
  const [changing, setChanging] = useState(false)
  const [draft, setDraft] = useState(closeDay(deal.expectedClose) ?? '')
  const [busy, setBusy] = useState(false)
  // The Keep this session made: the sentence shows in the card's place until
  // the close date moves (then the rules decide again).
  const [keptFor, setKeptFor] = useState<string | null>(null)
  const current = closeDay(deal.expectedClose)
  // Change / Keep are records-level deal writes (lib/writeAccess): without
  // access the check still reads, with no actions.
  const { records } = useWriteAccess()

  if (keptFor && keptFor === current) {
    return <div className="k-card px-5 py-4 text-13 text-ink-secondary" data-close-date-kept>{KEPT_SENTENCE}</div>
  }
  if (!view) return null

  async function keep() {
    if (busy) return
    setBusy(true)
    try {
      // The request's promise goes to onApply as it starts (§40.12 order).
      const write = confirmDealCloseDate(deal.id)
      await onApply(write)
      setKeptFor(closeDay((await write).expectedClose))
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to keep the close date'))
    } finally {
      setBusy(false)
    }
  }
  async function saveDate() {
    if (busy || !draft || draft === current) { setChanging(false); return }
    setBusy(true)
    try {
      await onApply(updateDeal(deal.id, { expectedClose: `${draft}T00:00:00Z` }))
      setChanging(false)
      toast.success('Close date updated')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update the close date'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="k-card p-5 flex flex-col gap-3" data-close-date-card>
      <div className="text-12 font-semibold inline-flex items-center gap-1.5" style={{ color: info.text }}>
        <Sparkles size={12} aria-hidden="true" /> Close date check
      </div>
      <div className="flex items-center gap-3">
        <div className="w-[46px] h-[50px] rounded-[10px] border border-edge-control flex flex-col items-center justify-center flex-shrink-0" aria-hidden="true">
          <div className="text-[10px] font-bold leading-none" style={{ color: danger.text }}>{view.month}</div>
          <div className="text-20 font-semibold text-ink-primary leading-tight">{view.day}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-ink-primary leading-snug" data-close-date-line>{view.dateLine}</div>
          <div className="text-13 text-ink-muted" data-close-date-reason>{view.reason}</div>
        </div>
      </div>
      {!records ? null : changing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" aria-label="New close date" value={draft} onChange={e => setDraft(e.target.value)}
            className="h-9 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent" />
          <button type="button" className="k-btn-quiet" disabled={busy} onClick={() => { setChanging(false); setDraft(current ?? '') }}>Cancel</button>
          <button type="button" className="k-btn disabled:opacity-60" disabled={busy || !draft} onClick={() => { void saveDate() }}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="k-btn-secondary" disabled={busy} onClick={() => setChanging(true)}>Change close date</button>
          <button type="button" className="k-btn-secondary" disabled={busy} onClick={() => { void keep() }}>{view.keepLabel}</button>
        </div>
      )}
    </div>
  )
}
